import { fileHref } from '../lib/fileAccess'
import { useState, useEffect, useMemo } from 'react'
import { ARCHIVABLE, missingArchive } from '../lib/archive'
import html2pdf from 'html2pdf.js'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { shipTodayAuto } from '../lib/shipTime'
import { canSeeOthers } from '../lib/crewPrivacy'
import { filePath } from '../lib/filePaths'
import { ELEMENTS, GRADE_LABEL, GRADE_SHORT } from '../lib/ismCore'
import { SURVEY_TYPES, SECTIONS, OWN_SECTION, STATUS_LABEL, surveySources, evaluate, todoPlan, summaryLine, addDays, MISSING_TABLE, TODO_LISTS,
  openSurvey, checkedBy, ownLinesFor, nextDueList, surveyTypeShort, SURVEY_STATUS_LABEL, SURVEY_RESULTS, SURVEY_RESULT_LABEL, NEXT_DUE_MONTHS,
  SURVEY_FINDING_GRADES, REPORT_DOC_TYPE, idsFor, surveyName } from '../lib/surveyCore'
import { buildSurveyHtml, statusText } from '../lib/surveyPdf'
import HistoryLink from '../components/HistoryLink'
import GuideButton from '../components/GuideVideo'

// Survey readiness: pick a survey, every checklist line is filled from the vessel's records (src/lib/surveyCore.js - the same
// rules as the AI's get_survey_readiness), gaps in red with a Fix link, one click makes the To Dos, PDF for the surveyor.
// Needs Documents view (App / AppShell); every other line only with that module's view, crew certificates only with the
// "Crew personal data" right. Saved in the database (sql/feat-10b.sql), seen by every login and the AI:
//   - the surveys (planned / in progress / done: surveyor, organisation, place, result, report, next due) with their history;
//   - ticks for on-board checks and "not applicable", on the OPEN survey of the type (the first tick starts a planned one);
//   - the vessel's own checklist lines per survey type (they count in the score);
//   - survey findings = ISM findings (Condition of Class / Inspection deficiency) linked to the survey.
// Writing needs Documents Edit (delete a survey: Full); findings need ISM Audits Edit.
const TONE = { ok: ['#1f6b3a', 'rgba(31,107,58,.12)'], due: ['#8a5c0a', 'rgba(201,147,58,.14)'], missing: ['#b4313c', 'rgba(192,67,74,.14)'], na: ['var(--muted)', 'rgba(125,138,153,.12)'], check: ['#2a6a96', 'rgba(106,170,212,.12)'] }
// pages that open filtered to a record from hc_open_q (App takeOpenQuery: the DataTable pages); Documents uses hc_open_doc
const OPENS_QUERY = ['equipment', 'mtask']
const SURVEY_COLS = surveySources('2000-01-01').find(s => s.table === 'vessel_surveys').select
// ticks from before they were saved in the database: kept in this browser only (moved into the survey with one click)
const oldTickKey = (vessel, type) => 'hc_survey_ticks_' + vessel + '_' + type
const readOldTicks = (vessel, type) => { try { return JSON.parse(localStorage.getItem(oldTickKey(vessel, type)) || '{}') || {} } catch (e) { return {} } }
const isoAdd = (d, months) => { const x = new Date(String(d).slice(0, 10) + 'T12:00:00Z'); x.setUTCMonth(x.getUTCMonth() + months); return x.toISOString().slice(0, 10) }

export default function SurveyReadiness({ onNavigate }) {
  const { config, can, accountType } = useAuth()
  const today = shipTodayAuto()
  const [type, setType] = useState(() => { try { return localStorage.getItem('hc_survey_type') || 'flag_private' } catch (e) { return 'flag_private' } })
  const [surveyDate, setSurveyDate] = useState(() => addDays(today, 30))
  const [rows, setRows] = useState(null)
  const [findings, setFindings] = useState([])
  const [unreadable, setUnreadable] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [editSurvey, setEditSurvey] = useState(null)   // survey row being edited ({} = new)
  const [findingFor, setFindingFor] = useState(null)   // survey the new finding belongs to
  const [ownEdit, setOwnEdit] = useState(false)
  const [focus, setFocus] = useState(null)             // a survey opened from a notice

  const allowed = m => !can || can(m, 'read')
  const personal = canSeeOthers(accountType, config?.permissions)
  const mayTodo = !can || can('todo', 'write')
  const mayEdit = !can || can('documents', 'write')
  const mayDelete = !can || can('documents', 'full')
  const mayFinding = !can || can('ism_audits', 'write')

  useEffect(() => { try { localStorage.setItem('hc_survey_type', type) } catch (e) {} }, [type])
  useEffect(() => { if (config?.id) load() }, [config?.id]) // eslint-disable-line

  async function load() {
    setLoading(true); setMsg('')
    const out = {}, bad = new Set()
    const srcs = surveySources(today).filter(s => allowed(s.module) && (!s.personal || personal))
    await Promise.all(srcs.filter(s => !s.noClient).map(async s => {
      let q = supabase.from(s.table).select(s.select).eq('client_id', config.id).limit(s.limit)
      if (s.eq) q = q.eq(s.eq.field, s.eq.value)
      if (s.range) q = q.gte(s.range.field, s.range.from).lt(s.range.field, s.range.to)
      if (s.order) { const [f, dir] = s.order.split('.'); q = q.order(f, { ascending: dir !== 'desc' }) }
      // archived equipment / tasks are not part of the readiness (src/lib/archive.js); no column yet -> unfiltered
      let { data, error } = ARCHIVABLE.includes(s.table) ? await q.is('archived_at', null) : await q
      if (error && ARCHIVABLE.includes(s.table) && missingArchive(error.message)) ({ data, error } = await supabase.from(s.table).select(s.select).eq('client_id', config.id).limit(s.limit))
      if (error && !s.optional && !MISSING_TABLE.test(error.code + ' ' + error.message)) bad.add(s.module)
      out[s.table] = error ? [] : (data || [])
    }))
    // this month's LSA / FFE checks: a child table, read by item ids (like the LSA page)
    const chk = srcs.find(s => s.table === 'lsa_checks'), ids = (out.lsa_items || []).map(i => i.id)
    out.lsa_checks = []
    if (chk) for (let k = 0; k < ids.length; k += 150) {
      const { data, error } = await supabase.from('lsa_checks').select(chk.select).eq(chk.eq.field, chk.eq.value).in('item_id', ids.slice(k, k + 150))
      if (error) bad.add('lsa'); else out.lsa_checks.push(...(data || []))
    }
    // the To Dos the ISM findings point at: one that was deleted is no To Do (the gap comes back)
    const tds = srcs.find(s => s.byIds), tids = idsFor(tds, out), got = []
    let tdOk = !!tds
    for (let k = 0; tds && k < tids.length; k += 150) {
      const { data, error } = await supabase.from(tds.table).select(tds.select).in('id', tids.slice(k, k + 150))
      if (error) { tdOk = false; break } got.push(...(data || []))
    }
    if (tdOk) out.todo_items = got
    // the findings of the saved surveys (ISM findings linked to a survey)
    if (allowed('ism_audits')) {
      const { data } = await supabase.from('ism_findings').select('id,ref,grade,element,description,status,due_date,owner,survey_id').eq('client_id', config.id).not('survey_id', 'is', null).limit(1000)
      setFindings(data || [])
    }
    setRows(out); setUnreadable([...bad]); setLoading(false)
    // opened from a notice ("survey due in 30 days"): that survey's type
    try {
      const want = localStorage.getItem('hc_survey_open')
      if (want) {
        localStorage.removeItem('hc_survey_open')
        const s = (out.vessel_surveys || []).find(x => x.id === want)
        if (s && s.survey_type !== 'other') setType(s.survey_type)
        if (s) setFocus(s.id)
      }
    } catch (e) {}
  }

  const surveys = rows?.vessel_surveys || []
  const current = openSurvey(surveys, type)
  // the open survey's date is the survey date (changed here = saved on it)
  const dateUsed = current && current.survey_date && current.survey_date >= today ? current.survey_date : surveyDate
  const result = useMemo(() => rows ? evaluate(rows, { type, surveyDate: dateUsed, today, allowed, personal, unreadable }) : null, [rows, type, dateUsed, today, unreadable]) // eslint-disable-line
  const who = useMemo(() => checkedBy(rows?.vessel_survey_checks, current?.id), [rows, current])
  const ticks = useMemo(() => Object.fromEntries((rows?.vessel_survey_checks || []).filter(c => current && c.survey_id === current.id).map(c => [c.line_id, c.state])), [rows, current])
  const due = useMemo(() => nextDueList(surveys, today), [surveys, today])
  const patchRows = (table, fn) => setRows(r => ({ ...r, [table]: fn(r[table] || []) }))
  const [oldTicks, setOldTicks] = useState({})
  useEffect(() => { setOldTicks(config?.id ? readOldTicks(config.id, type) : {}) }, [config?.id, type])

  // a planned survey of this type (the first tick, or "Start survey") - one open survey per type (unique in the database)
  async function startSurvey(status) {
    const snap = result ? { readiness_pct: result.score.pct, readiness_summary: summaryLine(result) } : {}
    if (current) {
      if (current.status === status) return current
      const { data, error } = await supabase.from('vessel_surveys').update({ status, ...snap }).eq('id', current.id).select(SURVEY_COLS).single()
      if (error) throw error
      patchRows('vessel_surveys', l => l.map(x => x.id === data.id ? data : x))
      return data
    }
    const { data, error } = await supabase.from('vessel_surveys').insert({ client_id: config.id, survey_type: type, status, survey_date: dateUsed, ...snap }).select(SURVEY_COLS).single()
    if (error) { if (error.code === '23505') { await load(); return null } throw error }   // someone else started it just now
    patchRows('vessel_surveys', l => [data, ...l])
    return data
  }

  async function setTick(id, v) {
    if (!mayEdit) return
    setMsg('')
    try {
      const s = current || await startSurvey('planned')
      if (!s) { setMsg('Someone started this survey just now - the list is reloaded, tick again.'); return }
      if (v) {
        const { data, error } = await supabase.from('vessel_survey_checks').upsert({ client_id: config.id, survey_id: s.id, line_id: id, state: v }, { onConflict: 'survey_id,line_id' }).select('id,survey_id,line_id,state,set_by_name,set_at').single()
        if (error) throw error
        patchRows('vessel_survey_checks', l => [...l.filter(c => !(c.survey_id === s.id && c.line_id === id)), data])
      } else {
        const { error } = await supabase.from('vessel_survey_checks').delete().eq('survey_id', s.id).eq('line_id', id)
        if (error) throw error
        patchRows('vessel_survey_checks', l => l.filter(c => !(c.survey_id === s.id && c.line_id === id)))
      }
    } catch (e) { setMsg('Not saved: ' + (e.message || e)) }
  }

  // the ticks this browser kept before (hc_survey_ticks_<vessel>_<type>): onto the open survey, then removed from the browser
  async function moveOldTicks() {
    setBusy(true); setMsg('')
    try {
      const s = current || await startSurvey('planned')
      if (!s) { setMsg('Someone started this survey just now - the list is reloaded, try again.'); return }
      const rowsIn = Object.entries(oldTicks).filter(([id, v]) => /^[a-z0-9_]{2,60}$/.test(id) && (v === 'ok' || v === 'na') && !ticks[id]).map(([id, v]) => ({ client_id: config.id, survey_id: s.id, line_id: id, state: v }))
      if (rowsIn.length) {
        const { data, error } = await supabase.from('vessel_survey_checks').upsert(rowsIn, { onConflict: 'survey_id,line_id' }).select('id,survey_id,line_id,state,set_by_name,set_at')
        if (error) throw error
        patchRows('vessel_survey_checks', l => [...l.filter(c => !(data || []).some(d => d.survey_id === c.survey_id && d.line_id === c.line_id)), ...(data || [])])
      }
      try { localStorage.removeItem(oldTickKey(config.id, type)) } catch (e) {}
      setOldTicks({}); setMsg(rowsIn.length + ' tick' + (rowsIn.length === 1 ? '' : 's') + ' from this device saved on the survey for everyone.')
    } catch (e) { setMsg('Not moved: ' + (e.message || e)) } finally { setBusy(false) }
  }

  async function changeDate(d) {
    const v = d && d >= today ? d : today
    if (current && mayEdit) {
      const { data, error } = await supabase.from('vessel_surveys').update({ survey_date: v }).eq('id', current.id).select(SURVEY_COLS).single()
      if (error) { setMsg('Date not saved: ' + error.message); return }
      patchRows('vessel_surveys', l => l.map(x => x.id === data.id ? data : x))
    } else setSurveyDate(v)
  }

  async function startNow() {
    setBusy(true); setMsg('')
    try { const s = await startSurvey('in_progress'); if (s) setMsg('Survey started: ' + surveyTypeShort(s.survey_type) + ' on ' + s.survey_date + ' (' + result.score.pct + '% ready).') }
    catch (e) { setMsg('Not started: ' + (e.message || e)) } finally { setBusy(false) }
  }
  function finishNow() {
    const d = current?.survey_date || dateUsed, held = d > today ? today : d
    setEditSurvey({ ...(current || { survey_type: type }), status: 'done', survey_date: held, next_due: current?.next_due || (NEXT_DUE_MONTHS[type] ? isoAdd(held, NEXT_DUE_MONTHS[type]) : '') })
  }

  // "Fix": the page that holds the record, opened on it where the page can do that
  function fix(f) {
    if (!f || !onNavigate) return
    try {
      if (f.page === 'docs' && f.query) localStorage.setItem('hc_open_doc', f.query)
      else if (f.query && OPENS_QUERY.includes(f.page)) localStorage.setItem('hc_open_q', JSON.stringify({ page: f.page, q: f.query }))
    } catch (e) {}
    onNavigate(f.page)
  }

  async function createTodos() {
    if (!result || !mayTodo) return
    setBusy(true); setMsg('')
    try {
      // the open To Dos of those lists: a gap that already has one is not made twice
      const { data: lists } = await supabase.from('todo_lists').select('id,name').eq('client_id', config.id).in('name', TODO_LISTS)
      const lids = (lists || []).map(l => l.id)
      const { data: openItems } = lids.length ? await supabase.from('todo_items').select('text').in('list_id', lids).eq('done', false).limit(3000) : { data: [] }
      const plan = todoPlan(result, (openItems || []).map(i => i.text))
      if (!plan.length) { setMsg('Every gap already has an open To Do.'); return }
      if (!window.confirm('Create ' + plan.length + ' To Do' + (plan.length === 1 ? '' : 's') + ' for the gaps (Renewals / Defects' + (plan.some(p => p.ismFindingId) ? ' / ISM Findings' : '') + ')?')) return
      const listIds = {}, made = { n: 0 }, failed = []
      for (const p of plan) {
        if (p.ismFindingId) {
          // the ISM module's own To Do: on "ISM Findings", linked to the finding (sql/ism-audits.sql)
          const { error } = await supabase.rpc('ism_finding_todo', { p_finding: p.ismFindingId })
          if (error) failed.push(p.text + ': ' + error.message); else made.n++
          continue
        }
        if (!listIds[p.list]) {
          // one shared list per name: found or made by the database (todo_list_for, sql/test-fixes-2.sql)
          const { data: id, error } = await supabase.rpc('todo_list_for', { p_client: config.id, p_name: p.list })
          if (error || !id) { failed.push(p.text + ': ' + (error ? error.message : 'no list')); continue }
          listIds[p.list] = id
        }
        const { error } = await supabase.from('todo_items').insert({ list_id: listIds[p.list], text: p.text, notes: p.notes, priority: p.priority, due_date: p.due_date, done: false, created_by: config?.crewName || null })
        if (error) failed.push(p.text + ': ' + error.message); else made.n++
      }
      setMsg(made.n + ' To Do' + (made.n === 1 ? '' : 's') + ' created' + (failed.length ? ' · ' + failed.length + ' not made: ' + failed.slice(0, 3).join('; ') : '') + '.')
    } catch (e) { setMsg('To Dos not created: ' + (e.message || e)) } finally { setBusy(false) }
  }

  function exportPdf() {
    if (!result) return
    const el = document.createElement('div')
    el.innerHTML = buildSurveyHtml(result, { vessel: config?.vessel_name || 'Vessel', preparedBy: config?.crewName || '', printed: today })
    document.body.appendChild(el)
    const fname = ('Survey_readiness_' + result.short + '_' + result.surveyDate).replace(/[^a-z0-9]+/gi, '_') + '.pdf'
    const opt = { margin: 8, filename: fname, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, windowWidth: 960, width: 960 }, pagebreak: { mode: ['css', 'legacy'] }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
    html2pdf().set(opt).from(el).save().then(() => document.body.removeChild(el), () => document.body.removeChild(el))
  }

  const pill = st => ({ display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', padding: '3px 9px', borderRadius: 11, textTransform: 'uppercase', whiteSpace: 'nowrap', color: (TONE[st] || TONE.na)[0], background: (TONE[st] || TONE.na)[1] })
  const s = result?.score
  const gapCount = result ? result.gaps.length : 0
  const barColor = !s ? 'var(--muted)' : s.pct >= 90 ? '#1f6b3a' : s.pct >= 70 ? '#8a5c0a' : '#b4313c'
  const small = { fontSize: 11, color: 'var(--muted)' }
  const linkBtn = { background: 'none', border: '0.5px solid var(--gold2)', color: 'var(--gold2)', fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 7, cursor: 'pointer' }
  const plainBtn = { ...linkBtn, borderColor: 'var(--line)', color: 'var(--muted)' }
  const sectionHead = { fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text)', borderBottom: '2px solid var(--line)', paddingBottom: 5, marginBottom: 4 }
  const tickBtns = l => (
    <>
      <button disabled={!mayEdit} title={who[l.id] ? 'Ticked by ' + who[l.id] : ''} style={{ ...plainBtn, color: ticks[l.id] === 'ok' ? '#1f6b3a' : 'var(--muted)' }} onClick={() => setTick(l.id, ticks[l.id] === 'ok' ? null : 'ok')}>{ticks[l.id] === 'ok' ? '✓ ' + (l.src === 'own' ? 'Done' : 'Checked') : l.src === 'own' ? 'Mark done' : 'Mark checked'}</button>
      <button disabled={!mayEdit} style={plainBtn} onClick={() => setTick(l.id, ticks[l.id] === 'na' ? null : 'na')}>{ticks[l.id] === 'na' ? 'Undo N/A' : 'N/A'}</button>
    </>
  )
  const sectionsShown = [...SECTIONS, ...new Set((result?.lines || []).map(l => l.section).filter(x => !SECTIONS.includes(x)))]

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '4px 4px 40px' }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ flex: '1 1 280px', minWidth: 0 }}>
          <div style={small}>Survey</div>
          <select value={type} onChange={e => setType(e.target.value)} style={{ width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
            {SURVEY_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
        <label style={{ flex: '0 1 170px' }}>
          <div style={small}>Survey date{current ? ' (saved on the survey)' : ''}</div>
          <input type="date" value={dateUsed} min={today} disabled={!!current && !mayEdit} onChange={e => changeDate(e.target.value)} style={{ width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }} />
        </label>
        <button className="btn" onClick={load} disabled={loading}>{loading ? 'Checking records...' : 'Refresh'}</button>
        <GuideButton page="survey_readiness" title="Survey Readiness - 1-min guide" style={{ alignSelf: 'center', fontSize: 12.5 }} />
      </div>

      {due.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {due.map(n => (
            <button key={n.type} onClick={() => setType(n.type)} style={{ ...plainBtn, color: n.days < 0 ? '#b4313c' : n.days <= 60 ? '#8a5c0a' : 'var(--muted)', fontWeight: 600 }}>
              Next {n.short} due {n.due} · {n.days < 0 ? Math.abs(n.days) + ' days overdue' : 'in ' + n.days + ' days'}
            </button>
          ))}
        </div>
      )}

      {result && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 16, marginBottom: 18, background: 'var(--card)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 34, fontWeight: 700, color: barColor, fontVariantNumeric: 'tabular-nums', minWidth: 90 }}>{s.pct}%</div>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Ready for the {result.short} survey on {result.surveyDate}</div>
              <div style={{ height: 8, borderRadius: 4, background: 'rgba(125,138,153,.18)', marginTop: 8, overflow: 'hidden' }}><div style={{ width: s.pct + '%', height: '100%', background: barColor }} /></div>
              <div style={{ ...small, marginTop: 6 }}>{s.ok} OK · {s.due} due soon · <span style={{ color: s.missing ? '#b4313c' : undefined, fontWeight: s.missing ? 700 : 400 }}>{s.missing} missing</span> · {s.na} not applicable{s.check ? ' · ' + s.check + ' to check on board' : ''}</div>
            </div>
          </div>
          <div style={{ ...small, marginTop: 10, lineHeight: 1.5 }}>Due soon = expires or falls due within {result.windowDays} days after the survey date. Score: OK counts 1, due soon ½, missing 0; on-board checks and lines not applicable are not counted. Own lines count: done = OK, not done yet = missing.</div>
          <div style={{ ...small, marginTop: 8 }}>
            {current
              ? <>Survey: <b style={{ color: 'var(--text)' }}>{SURVEY_STATUS_LABEL[current.status]}</b>{current.survey_date ? ' · ' + current.survey_date : ''}{current.place ? ' · ' + current.place : ''}{current.created_by_name ? ' · started by ' + current.created_by_name : ''}. Ticks are saved on it for everyone.</>
              : 'No survey of this type started yet. The first tick (or Start survey) starts a planned one, so the ticks are saved for everyone.'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {mayTodo && <button className="btn btn-primary" disabled={busy || !gapCount} onClick={createTodos}>{busy ? 'Working...' : gapCount ? 'Create To Dos for all gaps (' + gapCount + ')' : 'No gaps'}</button>}
            <button className="btn" onClick={exportPdf}>Export PDF</button>
            {mayEdit && (!current || current.status === 'planned') && <button className="btn" disabled={busy} onClick={startNow}>Start survey</button>}
            {mayEdit && current && <button className="btn" disabled={busy} onClick={finishNow}>Finish survey…</button>}
            {mayEdit && <button className="btn" onClick={() => setOwnEdit(o => !o)}>{ownEdit ? 'Close own lines' : 'Own lines…'}</button>}
          </div>
          {mayEdit && Object.keys(oldTicks).length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#8a5c0a' }}>
              {Object.keys(oldTicks).length} tick{Object.keys(oldTicks).length === 1 ? ' is' : 's are'} saved on this device only (from before ticks were shared).{' '}
              <button style={linkBtn} disabled={busy} onClick={moveOldTicks}>Save them on the survey</button>
            </div>
          )}
          {msg && <div style={{ marginTop: 10, fontSize: 12, color: /^Not |not /.test(msg) ? '#b4313c' : 'var(--muted)' }}>{msg}</div>}
        </div>
      )}
      {!result && <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>{loading ? 'Checking the records...' : ''}</div>}

      {ownEdit && result && <OwnLines type={type} lines={ownLinesFor(rows.survey_checklist_lines, type)} clientId={config.id} onChange={fn => patchRows('survey_checklist_lines', fn)} />}

      {result && sectionsShown.map(sec => {
        const ls = result.lines.filter(l => l.section === sec)
        if (!ls.length) return null
        return (
          <div key={sec} style={{ marginBottom: 18 }}>
            <div style={sectionHead}>{sec}</div>
            {ls.map(l => {
              const red = l.status === 'missing', amber = l.status === 'due'
              return (
                <div key={l.id} style={{ padding: '9px 4px', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: red ? '#b4313c' : 'var(--text)', fontWeight: red ? 600 : 500 }}>{l.label}{l.src === 'own' && <span style={{ ...small, marginLeft: 6 }}>(own line)</span>}</div>
                      <div style={{ fontSize: 12, color: red ? '#b4313c' : amber ? '#8a5c0a' : 'var(--muted)', marginTop: 2 }}>{l.detail}{who[l.id] ? ' · ' + who[l.id] : ''}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <span style={pill(l.status)}>{statusText(l.status)}</span>
                      {l.items && l.items.length > 0 && <button onClick={() => setOpen(o => o === l.id ? null : l.id)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 11, cursor: 'pointer' }}>{l.items.length} {open === l.id ? '▾' : '▸'}</button>}
                      {(red || amber) && l.fix && <button style={linkBtn} onClick={() => fix(l.fix)}>Fix</button>}
                      {l.src === 'manual' || l.src === 'own'
                        ? tickBtns(l)
                        : !l.hidden || ticks[l.id] === 'na' ? <button disabled={!mayEdit} title={ticks[l.id] === 'na' ? 'Count this line again' : 'This line does not apply to this vessel'} style={plainBtn} onClick={() => setTick(l.id, ticks[l.id] === 'na' ? null : 'na')}>{ticks[l.id] === 'na' ? 'Undo N/A' : 'N/A'}</button> : null}
                    </div>
                  </div>
                  {open === l.id && (
                    <div style={{ padding: '6px 0 2px 4px' }}>
                      {l.items.map((it, i) => <div key={i} style={{ fontSize: 12, color: /^OVERDUE|NOT SIGNED/.test(it) ? '#b4313c' : 'var(--text)', padding: '3px 10px', borderLeft: '2px solid var(--line)', wordBreak: 'break-word' }}>{it}</div>)}
                    </div>
                  )}
                  {(red || amber) && l.src !== 'own' && l.gaps.length > 0 && open !== l.id && l.gaps.length <= 3 && l.gaps.map((g, i) => (
                    <div key={i} style={{ fontSize: 11.5, color: g.status === 'missing' ? '#b4313c' : '#8a5c0a', marginTop: 3, wordBreak: 'break-word' }}>{'→ '}{g.text}</div>
                  ))}
                </div>
              )
            })}
          </div>
        )
      })}
      {result && <div style={{ ...small, marginTop: 8 }}>{summaryLine(result)}. A general checklist from flag / class practice for yachts, not an official form. Ticks and "N/A" are saved on the survey for every login. Status: {Object.values(STATUS_LABEL).join(' / ')}.</div>}

      {rows && (
        <div style={{ marginTop: 26 }}>
          <div style={{ ...sectionHead, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1 }}>Saved surveys</span>
            {mayEdit && <button style={{ ...linkBtn, textTransform: 'none', letterSpacing: 0 }} onClick={() => setEditSurvey({ survey_type: type, status: 'done', survey_date: today, next_due: NEXT_DUE_MONTHS[type] ? isoAdd(today, NEXT_DUE_MONTHS[type]) : '' })}>+ Record a survey</button>}
          </div>
          {!surveys.length && <div style={{ ...small, padding: '10px 4px' }}>No surveys saved yet. Start one from the checklist above, or record one that was held.</div>}
          {surveys.slice().sort((a, b) => String(b.survey_date || '9999').localeCompare(String(a.survey_date || '9999'))).map(sv => {
            const fs = findings.filter(f => f.survey_id === sv.id)
            return (
              <div key={sv.id} style={{ padding: '10px 4px', borderBottom: '1px solid var(--line)', background: focus === sv.id ? 'rgba(201,147,58,.08)' : undefined }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{surveyTypeShort(sv.survey_type)} · {sv.survey_date || 'no date'}{sv.place ? ' · ' + sv.place : ''}</div>
                    <div style={{ ...small, marginTop: 2 }}>
                      {[sv.surveyor, sv.organisation].filter(Boolean).join(', ') || 'Surveyor not recorded'}
                      {sv.readiness_pct != null ? ' · ' + sv.readiness_pct + '% ready when saved' : ''}
                      {sv.next_due ? ' · next due ' + sv.next_due : ''}
                      {sv.created_by_name ? ' · by ' + sv.created_by_name : ''}
                    </div>
                    {sv.notes && <div style={{ fontSize: 12, color: 'var(--text)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{sv.notes}</div>}
                    {fs.map(f => (
                      <div key={f.id} style={{ fontSize: 12, marginTop: 3, color: f.status === 'closed' ? 'var(--muted)' : '#8a5c0a' }}>
                        {'→ '}{f.ref || GRADE_SHORT[f.grade]} · {GRADE_SHORT[f.grade]} · {String(f.description || '').slice(0, 120)}{f.due_date ? ' · due ' + f.due_date : ''} · {f.status === 'closed' ? 'closed' : f.status === 'action_taken' ? 'action taken' : 'open'}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={pill(sv.status === 'done' ? (sv.result === 'not_passed' ? 'missing' : sv.result === 'passed_with_findings' || sv.result === 'postponed' ? 'due' : 'ok') : 'check')}>{sv.status === 'done' ? SURVEY_RESULT_LABEL[sv.result] || 'Done' : SURVEY_STATUS_LABEL[sv.status]}</span>
                    {sv.report_file && <a href={fileHref(sv.report_file)} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: 'var(--gold2)', textDecoration: 'none' }}>📎 Report</a>}
                    {sv.survey_type !== 'other' && sv.survey_type !== type && <button style={plainBtn} onClick={() => setType(sv.survey_type)}>Checklist</button>}
                    {mayFinding && <button style={plainBtn} onClick={() => setFindingFor(sv)}>+ Finding</button>}
                    {mayEdit && <button style={plainBtn} onClick={() => setEditSurvey(sv)}>Edit</button>}
                    <HistoryLink table="vessel_surveys" rowId={sv.id} label={surveyTypeShort(sv.survey_type) + ' ' + (sv.survey_date || '')} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {editSurvey && <SurveyForm survey={editSurvey} today={today} config={config} mayDelete={mayDelete} result={result}
        onClose={() => setEditSurvey(null)}
        onSaved={(row, deleted) => { patchRows('vessel_surveys', l => deleted ? l.filter(x => x.id !== row.id) : l.some(x => x.id === row.id) ? l.map(x => x.id === row.id ? row : x) : [row, ...l]); setEditSurvey(null) }} />}
      {findingFor && <FindingForm survey={findingFor} today={today} clientId={config.id} onClose={() => setFindingFor(null)} onSaved={f => { setFindings(l => [f, ...l]); setFindingFor(null) }} />}
    </div>
  )
}

const inp = { width: '100%', marginTop: 4, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' }
const lbl = { display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 10 }

function Dialog({ title, children, onClose, footer, error }) {
  useEffect(() => { const k = e => { if (e.key === 'Escape') onClose() }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k) }, [onClose])
  return (
    <div role="dialog" aria-modal="true" onClick={e => { if (e.target === e.currentTarget) onClose() }} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(4,10,20,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 12, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}><b style={{ flex: 1, fontSize: 15, color: 'var(--text)' }}>{title}</b><button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 17, cursor: 'pointer' }}>✕</button></div>
        {children}
        {error && <div className="err-box" style={{ margin: '4px 0 10px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>{footer}</div>
      </div>
    </div>
  )
}

// one saved survey: type, status, date, surveyor, organisation, place, result, report (uploaded as a document), notes, next due
function SurveyForm({ survey, today, config, mayDelete, result, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ survey_type: 'flag_private', status: 'done', ...survey }))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [uploading, setUploading] = useState(false)
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  const done = f.status === 'done'

  async function upload(file) {
    if (!file) return
    setUploading(true); setErr('')
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
      const path = filePath('documents', config.id, 'survey_report_' + Math.random().toString(36).slice(2, 7) + '.' + ext)
      const { error } = await supabase.storage.from('crew-docs').upload(path, file, { contentType: file.type || 'application/pdf', upsert: false })
      if (error) throw error
      // the report is a document in Documents (its file rules apply); it never counts as a certificate line
      const { data: doc, error: e2 } = await supabase.from('documents').insert({ client_id: config.id, title: 'Survey report: ' + surveyTypeShort(f.survey_type) + ' ' + (f.survey_date || today), document_type: REPORT_DOC_TYPE, folder: 'Surveys', file_url: path, status: 'No expiry' }).select('id,file_url').single()
      if (e2) throw e2
      setF(x => ({ ...x, report_doc_id: doc.id, report_file: doc.file_url }))
    } catch (e) { setErr('Upload failed: ' + (e.message || e)) } finally { setUploading(false) }
  }

  async function save() {
    setSaving(true); setErr('')
    const body = { survey_type: f.survey_type, status: f.status, survey_date: f.survey_date || null, surveyor: f.surveyor || null, organisation: f.organisation || null, place: f.place || null,
      result: done ? f.result || null : null, report_doc_id: f.report_doc_id || null, notes: f.notes || null, next_due: f.next_due || null }
    if (done && result && result.type === f.survey_type && f.readiness_pct == null) { body.readiness_pct = result.score.pct; body.readiness_summary = summaryLine(result) }
    const q = f.id ? supabase.from('vessel_surveys').update(body).eq('id', f.id) : supabase.from('vessel_surveys').insert({ ...body, client_id: config.id })
    const { data, error } = await q.select(SURVEY_COLS).single()
    setSaving(false)
    if (error) { setErr(error.code === '23505' ? 'A ' + surveyTypeShort(f.survey_type) + ' survey is already open - finish that one first.' : error.message); return }
    onSaved(data)
  }
  async function del() {
    if (!window.confirm('Delete "' + surveyName(f) + '" and its ticks? The change history keeps a record of it.')) return
    setSaving(true); setErr('')
    const { data, error } = await supabase.from('vessel_surveys').delete().eq('id', f.id).select('id')
    setSaving(false)
    if (error) { setErr(error.message); return }
    if (!data || !data.length) { setErr('Not deleted - this login may not delete surveys.'); return }
    onSaved({ id: f.id }, true)
  }

  return (
    <Dialog title={f.id ? 'Survey: ' + surveyTypeShort(f.survey_type) : 'Record a survey'} onClose={onClose} error={err}
      footer={<>
        {f.id && mayDelete && <button className="btn" style={{ marginRight: 'auto', color: '#b4313c' }} disabled={saving} onClick={del}>Delete</button>}
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={saving || uploading} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
      </>}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...lbl, flex: '1 1 220px' }}>Survey type
          <select style={inp} value={f.survey_type} disabled={!!f.id} onChange={e => set('survey_type', e.target.value)}>
            {SURVEY_TYPES.map(t => <option key={t.key} value={t.key}>{t.short}</option>)}<option value="other">Other survey</option>
          </select>
        </label>
        <label style={{ ...lbl, flex: '1 1 140px' }}>Status
          <select style={inp} value={f.status} onChange={e => set('status', e.target.value)}>
            {Object.entries(SURVEY_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...lbl, flex: '1 1 150px' }}>{done ? 'Date held' : 'Planned date'}<input type="date" style={inp} value={f.survey_date || ''} onChange={e => set('survey_date', e.target.value)} /></label>
        <label style={{ ...lbl, flex: '1 1 150px' }}>Next survey due<input type="date" style={inp} value={f.next_due || ''} onChange={e => set('next_due', e.target.value)} /></label>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...lbl, flex: '1 1 160px' }}>Surveyor<input style={inp} value={f.surveyor || ''} maxLength={120} onChange={e => set('surveyor', e.target.value)} /></label>
        <label style={{ ...lbl, flex: '1 1 160px' }}>Organisation<input style={inp} value={f.organisation || ''} maxLength={120} placeholder="Flag administration, class society, PSC..." onChange={e => set('organisation', e.target.value)} /></label>
      </div>
      <label style={lbl}>Place<input style={inp} value={f.place || ''} maxLength={120} placeholder="Port / yard" onChange={e => set('place', e.target.value)} /></label>
      {done && <label style={lbl}>Result
        <select style={inp} value={f.result || ''} onChange={e => set('result', e.target.value)}>
          <option value="">-</option>{SURVEY_RESULTS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </label>}
      <div style={lbl}>Surveyor's report
        <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {f.report_file ? <a href={fileHref(f.report_file)} target="_blank" rel="noreferrer" style={{ color: 'var(--gold2)', fontSize: 12.5 }}>📎 View report</a> : null}
          <label style={{ fontSize: 12.5, color: 'var(--text)', cursor: uploading ? 'wait' : 'pointer', border: '0.5px dashed var(--line)', borderRadius: 7, padding: '6px 10px' }}>
            {uploading ? 'Uploading...' : f.report_file ? 'Replace' : '📎 Upload PDF / photo'}
            <input type="file" accept="application/pdf,image/*" disabled={uploading} onChange={e => upload(e.target.files?.[0])} style={{ display: 'none' }} />
          </label>
          {f.report_file && <button onClick={() => setF(x => ({ ...x, report_doc_id: null, report_file: null }))} style={{ background: 'none', border: 'none', color: '#b4313c', fontSize: 12, cursor: 'pointer' }}>Remove</button>}
        </div>
        <div style={{ marginTop: 4 }}>Saved in Documents (folder Surveys).</div>
      </div>
      <label style={lbl}>Notes<textarea style={{ ...inp, minHeight: 70 }} value={f.notes || ''} maxLength={4000} onChange={e => set('notes', e.target.value)} /></label>
    </Dialog>
  )
}

// a survey finding = an ISM finding (Condition of Class / Inspection deficiency) linked to the survey
function FindingForm({ survey, today, clientId, onClose, onSaved }) {
  const [f, setF] = useState({ grade: survey.survey_type === 'class_annual' ? 'coc' : 'deficiency', element: 10, description: '', due_date: addDays(today, 30), owner: '' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  async function save() {
    setSaving(true); setErr('')
    const { data, error } = await supabase.from('ism_findings').insert({ client_id: clientId, survey_id: survey.id, grade: f.grade, element: Number(f.element), description: f.description.trim(), due_date: f.due_date || null, owner: f.owner.trim() || null, source: 'app' })
      .select('id,ref,grade,element,description,status,due_date,owner,survey_id').single()
    setSaving(false)
    if (error) { setErr(error.message); return }
    onSaved(data)
  }
  return (
    <Dialog title={'Finding: ' + surveyTypeShort(survey.survey_type) + ' ' + (survey.survey_date || '')} onClose={onClose} error={err}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save finding'}</button></>}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...lbl, flex: '1 1 200px' }}>Type
          <select style={inp} value={f.grade} onChange={e => set('grade', e.target.value)}>{SURVEY_FINDING_GRADES.map(g => <option key={g} value={g}>{GRADE_LABEL[g]}</option>)}</select>
        </label>
        <label style={{ ...lbl, flex: '1 1 200px' }}>ISM element
          <select style={inp} value={f.element} onChange={e => set('element', e.target.value)}>{ELEMENTS.map(([n, t]) => <option key={n} value={n}>{n} · {t}</option>)}</select>
        </label>
      </div>
      <label style={lbl}>What the surveyor found<textarea style={{ ...inp, minHeight: 80 }} value={f.description} onChange={e => set('description', e.target.value)} /></label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ ...lbl, flex: '1 1 150px' }}>Due date<input type="date" style={inp} value={f.due_date} onChange={e => set('due_date', e.target.value)} /></label>
        <label style={{ ...lbl, flex: '1 1 150px' }}>Who does it<input style={inp} value={f.owner} onChange={e => set('owner', e.target.value)} /></label>
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>Saved as an ISM finding (ISM Audits), linked to this survey: the corrective action, verification and To Do work as for any finding.</div>
    </Dialog>
  )
}

// the vessel's own checklist lines for one survey type: add / edit / remove / reorder
function OwnLines({ type, lines, clientId, onChange }) {
  const [label, setLabel] = useState('')
  const [section, setSection] = useState(OWN_SECTION)
  const [editing, setEditing] = useState(null)   // { id, label, section }
  const [err, setErr] = useState('')
  const cols = 'id,survey_type,label,section,notes,sort_order'
  async function add() {
    setErr('')
    const sort = lines.length ? Math.max(...lines.map(l => Number(l.sort_order) || 0)) + 10 : 10
    const { data, error } = await supabase.from('survey_checklist_lines').insert({ client_id: clientId, survey_type: type, label: label.trim(), section, sort_order: sort }).select(cols).single()
    if (error) { setErr(error.message); return }
    onChange(l => [...l, data]); setLabel('')
  }
  async function saveEdit() {
    setErr('')
    const { data, error } = await supabase.from('survey_checklist_lines').update({ label: editing.label.trim(), section: editing.section }).eq('id', editing.id).select(cols).single()
    if (error) { setErr(error.message); return }
    onChange(l => l.map(x => x.id === data.id ? data : x)); setEditing(null)
  }
  async function remove(line) {
    if (!window.confirm('Remove the line "' + line.label + '" from the ' + surveyTypeShort(type) + ' checklist?')) return
    const { data, error } = await supabase.from('survey_checklist_lines').delete().eq('id', line.id).select('id')
    if (error || !data || !data.length) { setErr(error ? error.message : 'Not removed - this login may not change the checklist.'); return }
    onChange(l => l.filter(x => x.id !== line.id))
  }
  // move one line up / down: the order is rewritten 10, 20, 30 ... for this type
  async function move(i, d) {
    const j = i + d
    if (j < 0 || j >= lines.length) return
    const next = lines.slice(); [next[i], next[j]] = [next[j], next[i]]
    const changed = next.map((l, k) => ({ ...l, sort_order: (k + 1) * 10 })).filter(l => Number(lines.find(x => x.id === l.id).sort_order) !== l.sort_order)
    setErr('')
    for (const l of changed) {
      const { error } = await supabase.from('survey_checklist_lines').update({ sort_order: l.sort_order }).eq('id', l.id)
      if (error) { setErr(error.message); return }
    }
    onChange(all => all.map(x => { const c = changed.find(y => y.id === x.id); return c ? { ...x, sort_order: c.sort_order } : x }))
  }
  const small = { fontSize: 11.5, background: 'none', border: '0.5px solid var(--line)', color: 'var(--muted)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }
  return (
    <div style={{ border: '1px dashed var(--line)', borderRadius: 12, padding: 14, marginBottom: 18 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Own lines - {surveyTypeShort(type)}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10 }}>Lines this vessel adds to the checklist (e.g. this year's flag campaign items). Tick them when done; they count in the score.</div>
      {lines.map((l, i) => (
        <div key={l.id} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
          {editing && editing.id === l.id
            ? <>
                <input style={{ ...inp, marginTop: 0, flex: '1 1 220px', width: 'auto' }} value={editing.label} onChange={e => setEditing(x => ({ ...x, label: e.target.value }))} />
                <select style={{ ...inp, marginTop: 0, flex: '0 1 200px', width: 'auto' }} value={editing.section} onChange={e => setEditing(x => ({ ...x, section: e.target.value }))}>{SECTIONS.map(x => <option key={x} value={x}>{x}</option>)}</select>
                <button style={small} onClick={saveEdit}>Save</button><button style={small} onClick={() => setEditing(null)}>Cancel</button>
              </>
            : <>
                <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13, color: 'var(--text)' }}>{l.label} <span style={{ fontSize: 11, color: 'var(--muted)' }}>· {l.section}</span></div>
                <button style={small} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                <button style={small} disabled={i === lines.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                <button style={small} onClick={() => setEditing({ id: l.id, label: l.label, section: SECTIONS.includes(l.section) ? l.section : OWN_SECTION })}>Edit</button>
                <button style={{ ...small, color: '#b4313c' }} onClick={() => remove(l)}>Remove</button>
                <HistoryLink table="survey_checklist_lines" rowId={l.id} label={l.label} />
              </>}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        <input style={{ ...inp, marginTop: 0, flex: '1 1 240px', width: 'auto' }} placeholder="New line, e.g. Flag campaign 2026: enclosed space entry records" value={label} maxLength={200} onChange={e => setLabel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && label.trim().length >= 3) add() }} />
        <select style={{ ...inp, marginTop: 0, flex: '0 1 200px', width: 'auto' }} value={section} onChange={e => setSection(e.target.value)}>{SECTIONS.map(x => <option key={x} value={x}>{x}</option>)}</select>
        <button className="btn" disabled={label.trim().length < 3} onClick={add}>Add line</button>
      </div>
      {err && <div style={{ marginTop: 8, fontSize: 12, color: '#b4313c' }}>{err}</div>}
    </div>
  )
}
